import clsx from 'clsx';
import { type FC, useCallback, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';

import { Route } from '@/routes';

/**
 * 13/16 weight 500 — Figma's `paragraph-13/medium`. Both are set explicitly:
 * the exported `paragraph-13` line-height is 19.5px against the frame's 16, and
 * the agent card uses the same size at 18. Raised upstream.
 *
 * The border is on every tab and never coloured — the sliding indicator draws
 * the underline now. It stays because it is what reserves the row's last pixel:
 * without it the indicator would sit outside the nav's box.
 */
const TAB =
  'flex items-center justify-center border-b border-transparent pb-4 text-[13px] font-medium leading-4';

const ACTIVE_TAB = 'text-[var(--ds-color-text-primary)]';

/**
 * Hovering an inactive tab brings its label to the active tab's colour, so the
 * two states meet rather than the hover inventing a third. Only the label
 * changes — the underline stays the selected tab's alone, and moving it on
 * hover would say the selection had already moved.
 */
const INACTIVE_TAB =
  'text-[color:var(--ds-color-raw-stone-600)] hover:text-[var(--ds-color-text-primary)]';

/** The underline, positioned over the tabs' own transparent bottom border. */
const INDICATOR = 'pointer-events-none absolute bottom-0 left-0 h-px bg-[var(--ds-color-bg-brand)]';

/**
 * The sub-tab switcher's spring, as a CSS easing.
 *
 * The pill switcher in the design system animates its thumb with `motion` at
 * `{ stiffness: 520, damping: 42, mass: 1 }`, and this is that exact curve
 * sampled: zeta works out at 0.921, just under critical, so it settles without
 * overshoot in 291ms and a `linear()` reproduces it rather than approximating
 * something it cannot.
 *
 * Derived rather than imported because `motion` is a dependency of
 * `react-components`, not of this app, and adding it here to move one underline
 * would be a dependency for a transition. If it is ever added for another
 * reason, this is the thing to replace — the values above are all it needs.
 */
const SLIDE_MS = 291;
const SLIDE_EASE =
  'linear(0, 0.0189, 0.0668, 0.1327, 0.2086, 0.2886, 0.3685, 0.4455, 0.5179, 0.5844, 0.6446, 0.6984, 0.7458, 0.7872, 0.8231, 0.8539, 0.8801, 0.9022, 0.9208, 0.9364, 0.9492, 0.9598, 0.9685, 0.9755, 0.9812, 0.9857, 0.9893, 0.9922, 0.9944, 0.9961, 0.9974, 0.9984, 0.9991)';

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface ProjectOverviewTabsProps {
  /** The :projectId param, for the Agents href. */
  projectId: string;
  /** Placement, owned by the header — see its TAB_ROW. */
  className?: string;
}

/**
 * The workspace's three tabs.
 *
 * A <nav> with aria-current, not role="tablist": these are three URLs, not three
 * panels behind one.
 *
 * `end` on every link: the Users tab carries its sub-tab in a query param rather
 * than a path segment, so nothing nests under any of these URLs today and the
 * three stay symmetrical.
 *
 * The underline is one element that slides between the tabs, matching the
 * sub-tab switcher inside the Users & Invites tab. It is measured rather than
 * declared, because the tabs are sized by their own translated labels.
 */
export const ProjectOverviewTabs: FC<ProjectOverviewTabsProps> = ({ projectId, className }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const navRef = useRef<HTMLElement | null>(null);
  const indicatorRef = useRef<HTMLSpanElement | null>(null);
  // The first placement jumps. There is nothing to slide from, and animating in
  // from x=0 would read as the underline flying in on every mount.
  const placed = useRef(false);

  const place = useCallback((slide: boolean) => {
    const nav = navRef.current;
    const indicator = indicatorRef.current;

    if (!nav || !indicator) return;

    // aria-current is NavLink's own output, so this asks the rendered DOM which
    // tab is current rather than re-deriving it from the URL.
    const active = nav.querySelector<HTMLElement>('[aria-current="page"]');

    // No current tab is reachable — a URL under the workspace that is not one of
    // the three. Better no underline than one parked on an arbitrary tab.
    if (!active) {
      indicator.style.opacity = '0';
      return;
    }

    indicator.style.transition =
      slide && placed.current && !prefersReducedMotion()
        ? `transform ${SLIDE_MS}ms ${SLIDE_EASE}, width ${SLIDE_MS}ms ${SLIDE_EASE}`
        : 'none';
    indicator.style.transform = `translateX(${active.offsetLeft}px)`;
    indicator.style.width = `${active.offsetWidth}px`;
    indicator.style.opacity = '1';

    placed.current = true;
  }, []);

  // Layout effect, so the underline is in place before the first paint rather
  // than appearing a frame later.
  useLayoutEffect(() => place(true), [pathname, place]);

  useLayoutEffect(() => {
    const nav = navRef.current;

    if (!nav) return;

    // Each tab, not the nav. A tab's width changes without the selection doing
    // — the webfont finishing loading is the ordinary case, and a locale with
    // longer labels the other — but the nav is sized by the column around it
    // and does not budge when its children grow. Observing the nav therefore
    // never fires, and the underline keeps a width measured in the fallback
    // font. Re-measured without sliding, since nothing was switched.
    const size = new ResizeObserver(() => place(false));

    for (const tab of nav.querySelectorAll('a')) size.observe(tab);

    return () => size.disconnect();
  }, [place]);

  return (
    <nav
      aria-label={t('project-overview.tabs-label')}
      className={clsx('relative flex gap-4', className)}
      ref={navRef}
    >
      <NavLink
        className={({ isActive }) => clsx(TAB, isActive ? ACTIVE_TAB : INACTIVE_TAB)}
        end
        to={`/${Route.PROJECTS}/${projectId}/${Route.AGENTS}`}
      >
        {t('project-overview.agents')}
      </NavLink>
      <NavLink
        className={({ isActive }) => clsx(TAB, isActive ? ACTIVE_TAB : INACTIVE_TAB)}
        end
        to={`/${Route.PROJECTS}/${projectId}/${Route.USERS}`}
      >
        {t('project-overview.users-and-invites')}
      </NavLink>
      <NavLink
        className={({ isActive }) => clsx(TAB, isActive ? ACTIVE_TAB : INACTIVE_TAB)}
        end
        to={`/${Route.PROJECTS}/${projectId}/${Route.SETTINGS}`}
      >
        {t('project-overview.settings')}
      </NavLink>

      <span aria-hidden="true" className={INDICATOR} ref={indicatorRef} style={{ opacity: 0 }} />
    </nav>
  );
};
